import {
  FOC_INITIAL_RESPONSE_TOUR_STEPS,
  type FocInitialResponseTourStepId,
  type FocInitialResponseTourSurface,
} from './focInitialResponseTourSteps';

/** Steps that open the IR Reports surface (map + openTab=Reports). */
const REPORTS_STEP_IDS = new Set<FocInitialResponseTourStepId>([
  'sec84',
  'ussit001',
  'ussit002',
  'ussit005',
  'ussit010',
]);

/**
 * Hosts (Initial Response page / New forms view) register UI moves the tour needs.
 */
export type FocInitialResponseTourApi = {
  getLocation?: () => { pathname: string; search: string };
  /** Force New IR view, close map, and select the editable ICS 201 card. */
  ensureNewIcs201: () => void;
  /** Open IR map layout with Reports selected. */
  openReportsSurface?: () => void;
  /** Select a forms-dropdown card by widget name when needed. */
  selectFormsCard?: (widgetName: string) => void;
  scrollToTour?: (tour: string) => void;
};

let api: Partial<FocInitialResponseTourApi> = {};
let pendingStep: FocInitialResponseTourStepId | null = null;

export function registerFocInitialResponseTourApi(
  partial: Partial<FocInitialResponseTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    if (currentSurface() === surfaceFor(step)) {
      pendingStep = null;
      prepareFocInitialResponseStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(
      partial
    ) as (keyof FocInitialResponseTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocInitialResponseTourApi(): Partial<FocInitialResponseTourApi> {
  return api;
}

function surfaceFor(
  id: FocInitialResponseTourStepId
): FocInitialResponseTourSurface {
  return (
    FOC_INITIAL_RESPONSE_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'initialResponse'
  );
}

function readLocation(): { pathname: string; search: string } {
  const fromHost = getFocInitialResponseTourApi().getLocation?.();
  if (fromHost) return fromHost;
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
    };
  }
  return { pathname: '/hub', search: '?openTab=Incidents' };
}

function currentSurface(): FocInitialResponseTourSurface {
  const { pathname } = readLocation();
  if (
    pathname.includes('/initial-response') ||
    pathname.includes('/dashboard') ||
    pathname.includes('/iap/')
  ) {
    return 'initialResponse';
  }
  return 'hub';
}

function ensureSurface(id: FocInitialResponseTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();
  if (needed === current || needed === 'initialResponse') {
    // Hub bootstrap navigates away; once on IAP IR we always prepare.
    if (needed === 'hub' && current !== 'hub') {
      pendingStep = id;
      return false;
    }
    pendingStep = null;
    return true;
  }
  pendingStep = id;
  return false;
}

export function isFocInitialResponseReportsStep(
  id: FocInitialResponseTourStepId
): boolean {
  return REPORTS_STEP_IDS.has(id);
}

export function prepareFocInitialResponseStep(
  id: FocInitialResponseTourStepId
) {
  if (!ensureSurface(id)) return;

  const host = getFocInitialResponseTourApi();
  if (isFocInitialResponseReportsStep(id)) {
    host.openReportsSurface?.();
  } else {
    host.ensureNewIcs201?.();
  }

  const def = FOC_INITIAL_RESPONSE_TOUR_STEPS.find((step) => step.id === id);
  if (def?.tour) {
    host.scrollToTour?.(def.tour);
  }
}

export function resetFocInitialResponseTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
